import { SensorConversions } from "../conversionFunctions/sensorConversions.js";
import { sensorParameterNumericValidators } from "../SensorConfigurationParameterHelpers.js";
import { SensorParameterConfigBase, SensorParameterValueType } from "../SensorConfigurationTypes.js";
import { SensorElementConfig } from "./types.js";

const pressureTransducerParams = {
    slopeKpaPerVolt: {
        key: "slopeKpaPerVolt",
        type: SensorParameterValueType.NUMBER,
        name: "Slope (kPa/V)",
        description: "Pressure gain: how many kPa the output increases per volt of input signal.",
        defaultValue: 1,
        validator: sensorParameterNumericValidators.isNumber,
        mustBeNonNegative: false,
        mustBeInteger: false,
        parameterUnitCategory: "pressureTransducerSlope",
    },
    offsetKpa: {
        key: "offsetKpa",
        type: SensorParameterValueType.NUMBER,
        name: "Offset (kPa)",
        description:
            "Pressure offset added to slope × voltage. Typically a negative value that zeros the output at the sensor's minimum voltage.",
        defaultValue: 0,
        validator: sensorParameterNumericValidators.isNumber,
        mustBeNonNegative: false,
        mustBeInteger: false,
        parameterUnitCategory: "liquidPressure",
    },
} as const satisfies Record<string, SensorParameterConfigBase<number>>;

export const pressureTransducerElementConfig: SensorElementConfig<
    typeof pressureTransducerParams,
    typeof SensorConversions.linearWithMinAndMax
> = {
    legalConvertedRange: [0, null],
    conversionKey: "linearWithMinAndMax",
    parameterConfigs: pressureTransducerParams,
    buildConversionParams: ({ slopeKpaPerVolt, offsetKpa }) => ({
        slope: slopeKpaPerVolt,
        offset: offsetKpa,
        outputMin: 0,
        outputMax: Number.MAX_VALUE,
    }),
    decodedUnits: "volt",
    convertedUnits: "kilopascal",
    unitCategory: "liquidPressure",
    testCases: [
        {
            // Keller 21Y / Honeywell 150 PSIG midscale:
            //   voltageMultiplier=37.5, voltageOffset=18.75, PSI→kPa=6.89476
            //   slope = 37.5 * 6.89476 = 258.5535 kPa/V
            //   offset = -18.75 * 6.89476 = -129.27675 kPa
            //   at 2.5 V: 258.5535 * 2.5 - 129.27675 = 517.107 kPa
            parameters: { slopeKpaPerVolt: 258.5535, offsetKpa: -129.27675 },
            decodedValue: 2.5,
            convertedValue: 517.107,
        },
        {
            // At minimum voltage (0.5 V for a 0.5–4.5V sensor), output should clamp to 0:
            //   258.5535 * 0.5 - 129.27675 = 129.27675 - 129.27675 = 0 kPa
            parameters: { slopeKpaPerVolt: 258.5535, offsetKpa: -129.27675 },
            decodedValue: 0.5,
            convertedValue: 0,
        },
        {
            // Below-zero input: clamped to 0 by outputMin
            parameters: { slopeKpaPerVolt: 258.5535, offsetKpa: -129.27675 },
            decodedValue: 0.2,
            convertedValue: 0,
            skipBackwardConversionCheck: true,
        },
    ],
} as const;
